// App-level live-presence provider. Opens ONE socket for the session, folds the
// presence frames into a map every screen surface reads, and refreshes a screen's
// snapshot list the instant a snapshot_ready arrives (the 5s poll stays as a
// fallback). When the socket can't run — demo mode aside, any failure — the map
// stays empty and every component silently renders its REST status instead.

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '../auth/session.tsx';
import { API_BASE, request } from '../api/client.ts';
import { qk } from '../api/keys.ts';
import { LiveSocket, wsUrlFromApiBase, type LiveConnection, type LiveStatus } from './live-socket.ts';
import { ScriptedLiveSocket } from './demo-socket.ts';
import { EMPTY_PRESENCE, reducePresence, type PresenceMap } from './presence.ts';
import type { InboundMessage } from './ws-messages.ts';
import type { ScreenListItem } from '@seencast/shared';

interface LivePresenceValue {
  presence: PresenceMap;
  status: LiveStatus;
}

const LivePresenceContext = createContext<LivePresenceValue>({
  presence: EMPTY_PRESENCE,
  status: 'stopped',
});

/** Live presence for the whole app. Read `presence` and merge it with the REST
 *  screen via the pure helpers in ./presence.ts. */
export function useLivePresence(): LivePresenceValue {
  return useContext(LivePresenceContext);
}

const DEMO = import.meta.env.VITE_ENABLE_MOCKS === 'true';

async function mintTicket(signal?: AbortSignal): Promise<string> {
  const res = await request<{ ticket: string }>('/ws-ticket', { method: 'POST', signal });
  return res.ticket;
}

export function LivePresenceProvider({
  children,
  connectionOverride,
}: {
  children: ReactNode;
  /** Test seam: supply a fake connection instead of the real/demo socket. */
  connectionOverride?: (onMessage: (msg: InboundMessage) => void) => LiveConnection;
}) {
  const { activeOrgId } = useSession();
  const qc = useQueryClient();
  const [presence, setPresence] = useState<PresenceMap>(EMPTY_PRESENCE);
  const [status, setStatus] = useState<LiveStatus>('stopped');

  // Keep the newest handlers in refs so the socket effect can stay keyed only on
  // org identity (a new socket per org) without tearing down on every render.
  const onMessageRef = useRef<(msg: InboundMessage) => void>(() => {});
  onMessageRef.current = (msg: InboundMessage) => {
    if (msg.type === 'snapshot_ready') {
      // Refresh that screen's snapshots immediately; the poll is the fallback.
      void qc.invalidateQueries({ queryKey: qk.snapshots(msg.screen_id) });
      return;
    }
    setPresence((prev) => reducePresence(prev, msg));
  };

  useEffect(() => {
    // Reset presence when the org changes — the new socket re-seeds it.
    setPresence(EMPTY_PRESENCE);

    let conn: LiveConnection;
    if (connectionOverride) {
      conn = connectionOverride((m) => onMessageRef.current(m));
      setStatus('open');
    } else if (DEMO) {
      conn = new ScriptedLiveSocket({
        onMessage: (m) => onMessageRef.current(m),
        getScreenIds: () => screenIdsFromCache(qc),
      });
      setStatus('open');
    } else {
      conn = new LiveSocket({
        mintTicket,
        wsUrl: (ticket) => wsUrlFromApiBase(API_BASE, ticket),
        onMessage: (m) => onMessageRef.current(m),
        onStatus: setStatus,
      });
    }
    conn.start();
    return () => conn.stop();
    // Keyed on org only: a new socket per org; qc + handler refs are stable.
  }, [activeOrgId]);

  const value = useMemo<LivePresenceValue>(() => ({ presence, status }), [presence, status]);
  return <LivePresenceContext.Provider value={value}>{children}</LivePresenceContext.Provider>;
}

/** Collect the unique screen ids currently in the react-query cache (any filter
 *  variant of the screens list). Used only to point the demo emitter at the real
 *  fleet — the live socket learns ids from the server. */
function screenIdsFromCache(qc: ReturnType<typeof useQueryClient>): string[] {
  const ids = new Set<string>();
  for (const [, data] of qc.getQueriesData<ScreenListItem[]>({ queryKey: ['screens'] })) {
    if (Array.isArray(data)) for (const s of data) if (s?.id) ids.add(s.id);
  }
  return [...ids];
}

// Exposed so component tests can inject a fixed presence map without a socket.
export { LivePresenceContext };
